import { useState } from 'react';
import { useMutation } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { Field } from '../../../components/Field.jsx';
import { ABRIR_INVENTARIO } from './gql.js';

const emptyItem = () => ({ productoId: '', observacion: '' });

export default function InventarioForm({ onCancel, onSaved }) {
  const [items, setItems] = useState([emptyItem()]);
  const [errors, setErrors] = useState({});
  const [formState, setFormState] = useState({
    sucursalId: '',
    fechaInicio: new Date().toISOString().slice(0, 16),
    observacion: '',
  });
  const [, abrirInventario] = useMutation(ABRIR_INVENTARIO);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: field === 'productoId' ? value.replace(/[^0-9]/g, '') : value,
      };
      return next;
    });
  };

  const handleAddItem = () => setItems((prev) => [...prev, emptyItem()]);
  const handleRemoveItem = (index) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!formState.sucursalId.trim()) {
      nextErrors.sucursalId = 'Indicá la sucursal.';
    }
    if (!formState.fechaInicio) {
      nextErrors.fechaInicio = 'La fecha de inicio es obligatoria.';
    }
    const validItems = items
      .map((item) => ({
        productoId: item.productoId.trim(),
        observacion: item.observacion.trim(),
      }))
      .filter((item) => item.productoId);
    const invalidProducto = validItems.some((item) => Number(item.productoId) <= 0);
    if (invalidProducto) {
      nextErrors.items = 'Revisá los productos cargados.';
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    try {
      await abrirInventario({
        input: {
          sucursalId: formState.sucursalId.trim(),
          fechaInicio: formState.fechaInicio,
          observacion: formState.observacion.trim() || null,
          items: validItems.map((item) => ({
            productoId: item.productoId,
            observacion: item.observacion || null,
          })),
        },
      });
      onSaved?.();
    } catch (error) {
      setErrors({ form: error.message });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Abrir inventario"
        subtitle="Definí la sucursal y fecha inicial para comenzar un inventario físico."
      />

      {errors.form ? (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{errors.form}</div>
      ) : null}

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          <Field label="Sucursal" hint={errors.sucursalId} className="col-span-12 md:col-span-4">
            <input
              value={formState.sucursalId}
              onChange={(event) => setFormState((prev) => ({ ...prev, sucursalId: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Fecha de inicio" hint={errors.fechaInicio} className="col-span-12 md:col-span-4">
            <input
              type="datetime-local"
              value={formState.fechaInicio}
              onChange={(event) => setFormState((prev) => ({ ...prev, fechaInicio: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Observación" className="col-span-12 md:col-span-12">
            <textarea
              value={formState.observacion}
              onChange={(event) => setFormState((prev) => ({ ...prev, observacion: event.target.value }))}
              rows={3}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Productos a contar</h2>
          <button
            type="button"
            onClick={handleAddItem}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-3 py-1.5 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Agregar
          </button>
        </div>
        {errors.items ? <p className="text-sm text-red-300">{errors.items}</p> : null}
        <div className="flex flex-col gap-4">
          {items.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)] p-4">
              <div className="col-span-12 md:col-span-4">
                <label className="mb-1 block text-xs font-medium text-muted">Producto ID</label>
                <input
                  value={item.productoId}
                  onChange={(event) => handleItemChange(index, 'productoId', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="mb-1 block text-xs font-medium text-muted">Observación</label>
                <input
                  value={item.observacion}
                  onChange={(event) => handleItemChange(index, 'observacion', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-12 md:col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="w-full rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-200 transition hover:bg-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                  disabled={items.length === 1}
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Abrir inventario
          </button>
        }
      />
    </form>
  );
}
